import { Alert, Flex } from 'antd';
import { useTranslation } from 'react-i18next';
import { spacing } from '../../theming';
import {
    comparisonPeriod,
    databaseProgress,
    databaseNote,
    formatLocal,
    hoursToFinish,
    showsRecordedSince,
} from '../../utils/vinExtraction';
import { labelStyle } from './vinExtractionStyles';
import StatDatabaseCard from './StatDatabaseCard';
import StatCards from './StatCards';
import SuccessFailedChart from './SuccessFailedChart';
import FailureReasons from './FailureReasons';

/** The period's picker heads what it filters, with what its changes are compared against (SC-QA-102). */
export const PeriodHeader = ({ controls, period }) => {
    const { t } = useTranslation('vinExtraction');

    return (
        <Flex align="center" justify="space-between" gap={spacing[2]} wrap="wrap" data-testid="vin-period-header">
            {controls}
            {period && (
                <span style={labelStyle}>{t('stats.comparedWith', { period: t(`stats.period.${period}`) })}</span>
            )}
        </Flex>
    );
};

/**
 * The ready stats: the all-time vehicle database card, then the period: its picker, the "recorded since" notice, the
 * five cards, the chart and the failure reasons.
 */
const StatsBody = ({ stats, settings, controls, onRetry }) => {
    const { t, i18n } = useTranslation('vinExtraction');
    const period = comparisonPeriod(stats.range.from, stats.range.to);
    const progress = databaseProgress(stats.current);

    return (
        <>
            <StatDatabaseCard
                progress={progress}
                cachedAt={stats.current.cachedAt}
                hoursToFinish={hoursToFinish(progress, settings)}
                pace={{ batches: settings?.concurrentBatches, cars: settings?.batchCarLimit }}
                note={databaseNote(progress, settings)}
                isRunning={settings?.state === 'running'}
                onRetry={onRetry}
            />
            <PeriodHeader controls={controls} period={period} />
            {showsRecordedSince(stats.recordedSince, stats.range.from) && (
                <Alert
                    type="info"
                    showIcon
                    message={t('stats.recordedSince', {
                        date: formatLocal(stats.recordedSince, 'D MMM YYYY HH:mm', i18n.language),
                    })}
                />
            )}
            <StatCards stats={stats} period={period} />
            <Flex gap={spacing[3]} wrap="wrap" align="stretch">
                <div style={{ flex: '3 1 480px', minWidth: 0 }}>
                    <SuccessFailedChart series={stats.series} bucket={stats.range.bucket} period={period} />
                </div>
                <div style={{ flex: '2 1 320px', minWidth: 0 }}>
                    <FailureReasons
                        reasons={stats.failureReasons}
                        failed={stats.totals.failed}
                        notFound={stats.totals.notFound}
                    />
                </div>
            </Flex>
        </>
    );
};

export default StatsBody;
